import type {
  Firestore,
  Transaction
} from 'firebase-admin/firestore'
import type {
  BillingMetrics,
  BillingInsightSnapshot,
  ReferralCode,
  ReferralLeaderboardEntry,
  SubscriptionPlan,
  UserQuota,
  UserQuotaUsage,
  UserSubscription
} from '@my-roadtrip/types'
import { FieldValue } from '../../core/database/firebase.js'
import { FREE_PLAN_LIMITS, findDefaultPlanByPriceId } from './billing.constants.js'
import type { FirestoreDoc, FirestoreQuery } from '../../common/types/firestore.js'

export interface UserBillingProfile {
  id: string
  email: string | null
  displayName: string | null
  planId: string
  subscription?: UserSubscription
  quota?: UserQuota
  stripeCustomerId?: string | null
  insightSnapshot?: BillingInsightSnapshot | null
}

type QuotaIncrement = Partial<Record<keyof UserQuotaUsage, number>>

function nowISO(): string {
  return new Date().toISOString()
}

export class BillingRepository {
  private readonly plansCollection = 'billingPlans'
  private readonly usersCollection = 'users'
  private readonly referralsCollection = 'referralCodes'
  private readonly referralStatsCollection = 'referralStats'

  constructor(private readonly firestore: Firestore) {}

  async listPlans(includeInactive = false): Promise<SubscriptionPlan[]> {
    const baseQuery = includeInactive
      ? this.firestore.collection(this.plansCollection)
      : this.firestore.collection(this.plansCollection).where('active', '==', true)

    const query = baseQuery as FirestoreQuery

    const snapshot = await query.orderBy('displayOrder', 'asc').get()
    return snapshot.docs.map((doc: FirestoreDoc) => ({
      id: doc.id,
      ...doc.data()
    })) as SubscriptionPlan[]
  }

  async getPlanById(planId: string): Promise<SubscriptionPlan | null> {
    const doc = await this.firestore.collection(this.plansCollection).doc(planId).get()
    if (!doc.exists) {
      return null
    }
    return { id: doc.id, ...doc.data() } as SubscriptionPlan
  }

  async findPlanByPriceId(priceId: string): Promise<SubscriptionPlan | null> {
    const snapshot = await this.firestore
      .collection(this.plansCollection)
      .where('stripePriceId', '==', priceId)
      .limit(1)
      .get()

    if (!snapshot.empty) {
      const doc = snapshot.docs[0] as FirestoreDoc
      return { id: doc.id, ...doc.data() } as SubscriptionPlan
    }

    const fallback = findDefaultPlanByPriceId(priceId)
    return fallback ?? null
  }

  async upsertPlan(plan: SubscriptionPlan): Promise<void> {
    const now = nowISO()
    const record: SubscriptionPlan = {
      ...plan,
      createdAt: plan.createdAt ?? now,
      updatedAt: now
    }
    await this.firestore.collection(this.plansCollection).doc(plan.id).set(record, { merge: true })
  }

  async deletePlan(planId: string): Promise<void> {
    await this.firestore.collection(this.plansCollection).doc(planId).delete()
  }

  async getUserBillingProfile(userId: string): Promise<UserBillingProfile | null> {
    const doc = await this.firestore.collection(this.usersCollection).doc(userId).get()
    if (!doc.exists) {
      return null
    }
    const data = doc.data() || {}

    const subscription = data.subscription as UserSubscription | undefined
    const quota = data.quotas as UserQuota | undefined

    return {
      id: doc.id,
      email: typeof data.email === 'string' ? data.email : null,
      displayName: typeof data.displayName === 'string' ? data.displayName : null,
      planId: typeof data.plan === 'string' ? data.plan : 'free',
      subscription: subscription ?? undefined,
      quota: quota ?? undefined,
      stripeCustomerId:
        typeof data.stripeCustomerId === 'string' && data.stripeCustomerId.length > 0
          ? data.stripeCustomerId
          : null,
      insightSnapshot: (data.billingInsights as BillingInsightSnapshot | undefined) ?? null
    }
  }

  async findUserByStripeCustomer(customerId: string): Promise<UserBillingProfile | null> {
    const snapshot = await this.firestore
      .collection(this.usersCollection)
      .where('stripeCustomerId', '==', customerId)
      .limit(1)
      .get()

    if (snapshot.empty) {
      return null
    }

    const doc = snapshot.docs[0] as FirestoreDoc
    const data = doc.data()
    return {
      id: doc.id,
      email: typeof data.email === 'string' ? data.email : null,
      displayName: typeof data.displayName === 'string' ? data.displayName : null,
      planId: typeof data.plan === 'string' ? data.plan : 'free',
      subscription: data.subscription as UserSubscription | undefined,
      quota: data.quotas as UserQuota | undefined,
      stripeCustomerId: customerId,
      insightSnapshot: (data.billingInsights as BillingInsightSnapshot | undefined) ?? null
    }
  }

  async updateUserSubscription(
    userId: string,
    subscription: UserSubscription,
    quota: UserQuota
  ): Promise<void> {
    const now = nowISO()
    await this.firestore.collection(this.usersCollection).doc(userId).set(
      {
        plan: subscription.planId,
        premium: subscription.tier !== 'free',
        subscriptionStatus: subscription.status,
        stripeCustomerId: subscription.stripeCustomerId ?? null,
        stripeSubscriptionId: subscription.stripeSubscriptionId ?? null,
        subscription: {
          ...subscription,
          updatedAt: now
        },
        quotas: {
          ...quota,
          updatedAt: now
        },
        updatedAt: now
      },
      { merge: true }
    )
  }

  async updateStripeCustomerId(userId: string, customerId: string): Promise<void> {
    await this.firestore.collection(this.usersCollection).doc(userId).set(
      {
        stripeCustomerId: customerId,
        updatedAt: nowISO()
      },
      { merge: true }
    )
  }

  async saveInsightSnapshot(userId: string, snapshot: BillingInsightSnapshot): Promise<void> {
    await this.firestore.collection(this.usersCollection).doc(userId).set(
      {
        billingInsights: snapshot,
        updatedAt: nowISO()
      },
      { merge: true }
    )
  }

  async incrementQuotaUsage(userId: string, increments: QuotaIncrement): Promise<UserQuota> {
    return await this.firestore.runTransaction<UserQuota>(async (tx: Transaction) => {
      const userRef = this.firestore.collection(this.usersCollection).doc(userId)
      const userDoc = await tx.get(userRef)
      const data = userDoc.data() || {}
      const quota = (data.quotas as UserQuota | undefined) ?? {
        limits: FREE_PLAN_LIMITS,
        usage: {
          plansGenerated: 0,
          aiCalls: 0,
          collaborators: 0,
          storageUsedMB: 0
        },
        window: {
          period: 'month',
          periodStart: nowISO(),
          periodEnd: nowISO()
        },
        updatedAt: nowISO()
      }

      const usage = quota.usage
      const updatedUsage = {
        plansGenerated: usage.plansGenerated + (increments.plansGenerated ?? 0),
        aiCalls: usage.aiCalls + (increments.aiCalls ?? 0),
        collaborators: usage.collaborators + (increments.collaborators ?? 0),
        storageUsedMB: usage.storageUsedMB + (increments.storageUsedMB ?? 0)
      }

      const updatedQuota: UserQuota = {
        ...quota,
        usage: updatedUsage,
        updatedAt: nowISO()
      }

      tx.update(userRef, { quotas: updatedQuota })
      return updatedQuota
    })
  }

  async resetQuota(userId: string, quota: UserQuota): Promise<void> {
    await this.firestore.collection(this.usersCollection).doc(userId).set(
      {
        quotas: {
          ...quota,
          updatedAt: nowISO()
        }
      },
      { merge: true }
    )
  }

  async listActiveSubscriptions(): Promise<UserBillingProfile[]> {
    const snapshot = await this.firestore
      .collection(this.usersCollection)
      .where('subscription.status', 'in', ['active', 'trialing'])
      .get()

    return snapshot.docs.map((doc: FirestoreDoc) => {
      const data = doc.data()
      return {
        id: doc.id,
        email: typeof data.email === 'string' ? data.email : null,
        displayName: typeof data.displayName === 'string' ? data.displayName : null,
        planId: typeof data.plan === 'string' ? data.plan : 'free',
        subscription: data.subscription as UserSubscription | undefined,
        quota: data.quotas as UserQuota | undefined,
        stripeCustomerId:
          typeof data.stripeCustomerId === 'string' ? data.stripeCustomerId : null
      }
    })
  }

  async saveReferralCode(code: ReferralCode): Promise<void> {
    await this.firestore.collection(this.referralsCollection).doc(code.code).set(code)
  }

  async getReferralCode(code: string): Promise<ReferralCode | null> {
    const doc = await this.firestore.collection(this.referralsCollection).doc(code).get()
    if (!doc.exists) return null
    return doc.data() as ReferralCode
  }

  async incrementReferralUsage(code: string): Promise<void> {
    await this.firestore.collection(this.referralsCollection).doc(code).set(
      {
        uses: FieldValue.increment(1),
        updatedAt: nowISO()
      },
      { merge: true }
    )
  }

  async listReferralLeaderboard(limit = 10): Promise<ReferralLeaderboardEntry[]> {
    const snapshot = await this.firestore
      .collection(this.referralStatsCollection)
      .doc('leaderboard')
      .collection('monthly')
      .orderBy('bonusEarned', 'desc')
      .limit(limit)
      .get()

    return snapshot.docs.map(
      (doc: FirestoreDoc) => doc.data() as ReferralLeaderboardEntry
    )
  }

  async saveBillingMetrics(metrics: BillingMetrics, periodKey: string): Promise<void> {
    await this.firestore
      .collection('billingMetrics')
      .doc(periodKey)
      .set(metrics, { merge: true })
  }
}
